'use client';

import { useCurrentViewportOrientation } from '@/hooks/use-current-viewport-orientation';
import { hasErrorField } from '@/utils/error';
import { MediaAssetOrientation } from '@calm/evexia-web-sdk';
import { SECOND_MS } from '@calm/web-common/dist/time';
import { useAutoHideControls } from '@rhg/hooks/use-auto-hide-controls';
import { useMediaControls } from '@rhg/hooks/use-media-controls';
import { useMediaTracker } from '@rhg/hooks/use-media-tracker';
import { MediaTrackerProps } from '@rhg/hooks/use-media-tracker/types';
import { useVideoPlayer } from '@rhg/hooks/use-video-player';
import { logger } from '@rhg/libs/logger';
import { Box } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { MediaEvent, getM3U8ProxyUrl } from '@rhg/utils/media';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MediaEl } from '../types';
import { VideoControls } from './controls';
import { VideoMinimalControls } from './minimal-controls';
import { VideoPlayerControls, VideoPlayerProps } from './types';

const HlsJs = dynamic(
    () =>
        import('@rhg/components/media/video-player/hls-js').then(
            (mod) => mod.HlsJs
        ),
    { ssr: false }
);

const styleVideoContainer = {
    width: '100%',
    maxHeight: '100dvh',
    position: 'relative',
    overflow: 'hidden',
};

export function VideoPlayer({
    videos,
    forcedOrientation,
    autoplay,
    muted,
    title,
    subtitle,
    showLoading = true,
    showPoster = true,
    showPosterOnEnd = false,
    hasAutoplayFocusTrap = false,
    controls = VideoPlayerControls.Full,
    willTrackProgress = false,
    previousProgress = 0,
    sx,
    videoJsSx,
    actionButton,
    shouldInterruptPlay,
    loop,
    trackFn: trackFn_,
    onEnd,
    onControlClick,
    onMediaElChange,
}: VideoPlayerProps) {
    const containerRef = useRef<Element | null>(null);
    const [mediaEl, setMediaEl] = useState<MediaEl>();
    const [chosenOrientation, setChosenOrientation] =
        useState<MediaAssetOrientation>();
    const [hasStartedAutoplay, setHasStartedAutoplay] = useState(false);
    const [hasAutoPlayRestriction, setHasAutoPlayRestriction] = useState(false);
    const { setAutoplayFocusTrap } = useVideoPlayer();

    const currentOrientation = useCurrentViewportOrientation();

    const video = useMemo(() => {
        if (!chosenOrientation) return;
        return (
            videos.find((video) => video.orientation === chosenOrientation) ??
            videos[0]
        );
    }, [chosenOrientation, videos]);
    const orientation = video?.orientation ?? 'landscape';

    const videoControls = useMediaControls({
        type: 'video',
        mediaEl: mediaEl ?? null,
        fullscreenContainerRef: containerRef,
        isInitiallyMuted: !!muted,
        shouldInterruptPlay,
    });

    useEffect(() => {
        onMediaElChange?.(mediaEl ?? null);
        return () => onMediaElChange?.(null);
    }, [mediaEl, onMediaElChange]);

    // Use the default 4s idle timeout for all video modes — minimal included.
    const { areControlsHidden } = useAutoHideControls({
        isPlaying: !videoControls.timingState.isPaused,
        targetRef: containerRef,
    });

    const { setTime } = videoControls;

    const trackFn = useCallback(
        (progress: MediaTrackerProps) => {
            if (
                progress.eventType === MediaEvent.LoadedMetadata &&
                previousProgress
            ) {
                setTime(previousProgress / SECOND_MS);
            }
            trackFn_?.(progress);
        },
        [previousProgress, setTime, trackFn_]
    );

    useMediaTracker({
        assetType: 'video',
        mediaEl,
        trackFn,
        willTrackProgress,
    });

    const willUseM3U8Proxy =
        video?.type === 'application/x-mpegURL' &&
        video?.needsCredentials &&
        video?.credentials;

    const onRequest = useCallback(
        (xhr: XMLHttpRequest) => {
            if (!willUseM3U8Proxy) {
                const credentials =
                    video?.needsCredentials && video?.credentials
                        ? `?${video?.credentials}`
                        : '';
                const open = xhr.open.bind(xhr);
                xhr.open = (
                    method: string,
                    url: string | URL,
                    async?: boolean,
                    username?: string | null,
                    password?: string | null
                ) => {
                    // `async` is optional here; undefined would be the same as
                    // omitting it, which XHR treats as async = true.
                    open(
                        method,
                        `${url}${credentials}`,
                        async ?? true,
                        username,
                        password
                    );
                };
            }
        },
        [video?.credentials, video?.needsCredentials, willUseM3U8Proxy]
    );

    const credentialSrc = useMemo(() => {
        if (!video?.src) return '';
        // Return proxy URL
        if (willUseM3U8Proxy) {
            return getM3U8ProxyUrl(video.src, video?.credentials ?? '');
        }

        const credentials =
            video?.needsCredentials && video?.credentials
                ? `?${video?.credentials}`
                : '';
        return `${video.src}${credentials}`;
    }, [
        video?.src,
        video?.credentials,
        video?.needsCredentials,
        willUseM3U8Proxy,
    ]);

    const needsAutoplayTrigger = useMemo(
        () =>
            hasAutoplayFocusTrap &&
            autoplay &&
            mediaEl?.paused &&
            !hasStartedAutoplay &&
            document.hasFocus(),
        [autoplay, hasAutoplayFocusTrap, hasStartedAutoplay, mediaEl?.paused]
    );

    const startAutoplay = useCallback(() => {
        setHasStartedAutoplay(true);
        if (needsAutoplayTrigger) {
            setTimeout(() => {
                const autoPlayPromise = mediaEl?.play();
                if (autoPlayPromise !== undefined) {
                    autoPlayPromise
                        .then(() => {
                            setHasAutoPlayRestriction(false);
                        })
                        .catch((error: unknown) => {
                            // play() rejects with a DOMException, which is not
                            // `instanceof Error` in some engines, so narrow by
                            // shape rather than constructor.
                            if (
                                hasErrorField(error, 'name') &&
                                error.name === 'NotAllowedError'
                            ) {
                                setHasAutoPlayRestriction(true);
                                logger.warn(
                                    '[video-player] autoplay blocked by browser',
                                    {
                                        error: hasErrorField(error, 'message')
                                            ? error.message
                                            : 'NotAllowedError',
                                    }
                                );
                            } else {
                                logger.error(
                                    '[video-player] autoplay failed',
                                    {},
                                    error
                                );
                            }
                        });
                }
            }, 100);
        }
    }, [mediaEl, needsAutoplayTrigger]);

    // Ensures that orientation is set only once
    useEffect(() => {
        if (chosenOrientation) return;
        setChosenOrientation(forcedOrientation ?? currentOrientation);
    }, [chosenOrientation, currentOrientation, forcedOrientation]);

    useEffect(() => {
        setAutoplayFocusTrap(() =>
            needsAutoplayTrigger ? startAutoplay : undefined
        );
    }, [startAutoplay, needsAutoplayTrigger, setAutoplayFocusTrap]);

    return (
        <Box ref={containerRef} sx={sxAppend(styleVideoContainer, sx)}>
            {(!video?.needsCredentials || video?.credentials) &&
                video?.src &&
                video?.type && (
                    <HlsJs
                        sx={videoJsSx}
                        src={credentialSrc}
                        type={video?.type}
                        orientation={orientation}
                        withCredentials={video?.needsCredentials}
                        options={{
                            loop,
                            autoplay: !!autoplay,
                            muted: !!muted,
                            controls: false,
                            showLoading,
                            poster: showPoster ? video?.poster : undefined,
                            onRequest,
                        }}
                        setMediaEl={setMediaEl}
                        onEnd={onEnd}
                        showPosterOnEnd={showPosterOnEnd}
                    />
                )}
            {mediaEl &&
                (controls === VideoPlayerControls.Minimal ? (
                    <VideoMinimalControls
                        controls={videoControls}
                        actionButton={actionButton}
                        onControlClick={onControlClick}
                        areControlsHidden={areControlsHidden}
                    />
                ) : controls === VideoPlayerControls.Full ? (
                    <VideoControls
                        controls={videoControls}
                        title={title}
                        subtitle={subtitle}
                        actionButton={actionButton}
                        onControlClick={onControlClick}
                        hasAutoPlayRestriction={hasAutoPlayRestriction}
                        areControlsHidden={areControlsHidden}
                    />
                ) : null)}
        </Box>
    );
}
